import { toast } from 'react-toastify';
import { TooltippedText } from './tooltipped-text';

export const copyIconButtonClasses =
  'tw:inline-flex tw:items-center tw:justify-center tw:bg-transparent tw:border-none tw:p-0 tw:cursor-pointer tw:text-brand-green-0 tw:hover:text-brand-green-1';

/** Copy `value` to the clipboard with a short toast; the shared behaviour behind the copy icons. */
export async function copyToClipboard(value: string, what = 'Copied') {
  try {
    await navigator.clipboard.writeText(value);
    toast.success(what, { autoClose: 1500 });
  } catch {
    toast.error('Could not copy to the clipboard');
  }
}

/**
 * A short identifier or contact value (NPI, phone, email…) as plain text with a copy
 * icon beside it. Renders nothing special for an empty value: the host decides its
 * empty placeholder. See .mex/SWEEPS.md.
 */
export function CopyableText({
  value,
  copiedMessage = 'Copied',
  className,
  children,
  'data-testid': testId,
}: {
  value: string;
  copiedMessage?: string;
  className?: string;
  /** Extra actions rendered after the copy icon (e.g. EmailContact's send icon). */
  children?: React.ReactNode;
  'data-testid'?: string;
}) {
  return (
    <span
      className={['tw:inline-flex tw:items-center tw:gap-1.5', className].filter(Boolean).join(' ')}
      data-testid={testId}
    >
      <span className="tw:break-all">{value}</span>
      <TooltippedText tooltipContent="Copy">
        <button
          type="button"
          className={copyIconButtonClasses}
          onClick={() => copyToClipboard(value, copiedMessage)}
          aria-label={`Copy ${value}`}
          data-testid="copy-value"
        >
          <i className="far fa-fw fa-copy tw:text-[11px]" />
        </button>
      </TooltippedText>
      {children}
    </span>
  );
}
